import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { Button, DateRangeFilter } from '@/components';

export interface InvoiceFilterValue {
  agingTiers: string[];
  issuedFrom: string; // YYYY-MM-DD
  issuedTo: string; // YYYY-MM-DD
}

export const EMPTY_FILTER: InvoiceFilterValue = {
  agingTiers: [],
  issuedFrom: '',
  issuedTo: '',
};

export const countActiveFilters = (v: InvoiceFilterValue): number =>
  v.agingTiers.length + (v.issuedFrom || v.issuedTo ? 1 : 0);

const sectionLabel =
  'mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400';

const toISODate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '');
const fromISODate = (s: string) => (s ? new Date(s) : null);

// Quick issue-date presets
const presetRange = (fromDaysAgo: number, toDaysAgo = 0) => ({
  start: dayjs().subtract(fromDaysAgo, 'day').format('YYYY-MM-DD'),
  end: dayjs().subtract(toDaysAgo, 'day').format('YYYY-MM-DD'),
});

const ISSUE_PRESETS: {
  label: string;
  range: () => { start: string; end: string };
}[] = [
  { label: 'Today', range: () => presetRange(0, 0) },
  { label: 'Yesterday', range: () => presetRange(1, 1) },
  { label: 'Last 7 Days', range: () => presetRange(7, 0) },
  { label: 'Last 30 Days', range: () => presetRange(30, 0) },
];

const presetChip = (active: boolean) =>
  `rounded-lg border px-3.5 py-[7px] text-[12.5px] font-semibold transition-colors ${
    active
      ? 'border-primary bg-primary text-static-white'
      : 'border-stroke-sub-300 text-text-sub-600 hover:border-primary'
  }`;

// Aging tier pill config — colored toggle pills matching the prototype style.
const AGING_PILLS: {
  tier: string;
  label: string;
  active: string;
  inactive: string;
}[] = [
  {
    tier: 'green',
    label: 'On track · 0–3d',
    active: 'bg-success-lighter border-success-base text-success-dark',
    inactive:
      'border-stroke-soft-200 text-text-sub-600 hover:border-success-base',
  },
  {
    tier: 'amber',
    label: 'Watch · 4–6d',
    active: 'bg-warning-lighter border-warning-base text-warning-dark',
    inactive:
      'border-stroke-soft-200 text-text-sub-600 hover:border-warning-base',
  },
  {
    tier: 'red',
    label: 'Alert · 7d+',
    active: 'bg-error-lighter border-error-base text-error-dark',
    inactive:
      'border-stroke-soft-200 text-text-sub-600 hover:border-error-base',
  },
];

const AGING_DOT: Record<string, string> = {
  green: 'bg-success-base',
  amber: 'bg-warning-base',
  red: 'bg-error-base',
};

interface InvoiceFilterProps {
  value: InvoiceFilterValue;
  onApply: (value: InvoiceFilterValue) => void;
}

const InvoiceFilter: React.FC<InvoiceFilterProps> = ({ value, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<InvoiceFilterValue>(value);

  // Re-seed the draft from the applied value each time the drawer opens.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const activeCount = countActiveFilters(value);

  const toggleTier = (v: string) =>
    setDraft((d) => ({
      ...d,
      agingTiers: d.agingTiers.includes(v)
        ? d.agingTiers.filter((x) => x !== v)
        : [...d.agingTiers, v],
    }));

  const handleIssueChange = (range: { from: Date | null; to: Date | null }) =>
    setDraft((d) => ({
      ...d,
      issuedFrom: toISODate(range.from),
      issuedTo: toISODate(range.to),
    }));

  const applyPreset = (r: { start: string; end: string }, active: boolean) =>
    setDraft((d) =>
      active
        ? { ...d, issuedFrom: '', issuedTo: '' }
        : { ...d, issuedFrom: r.start, issuedTo: r.end }
    );

  const apply = () => {
    onApply(draft);
    setOpen(false);
  };

  const reset = () => {
    setDraft(EMPTY_FILTER);
    onApply(EMPTY_FILTER);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-2 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-sub-600 hover:bg-bg-weak-50"
      >
        <i className="ri-filter-3-line" />
        Filter
        {activeCount > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-static-white">
            {activeCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-bg-strong-950/40"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />

            {/* Right drawer */}
            <div className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col bg-bg-white-0 shadow-card-lg">
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between border-b border-stroke-soft-200 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-strong-950">
                    Filter
                  </h2>
                  <p className="text-xs text-text-sub-600">Invoices</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-text-soft-400 hover:text-text-sub-600"
                  aria-label="Close filter"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-5">
                {/* Issue date */}
                <div>
                  <h4 className={sectionLabel}>Issue date</h4>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {ISSUE_PRESETS.map((p) => {
                      const r = p.range();
                      const active =
                        draft.issuedFrom === r.start &&
                        draft.issuedTo === r.end;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => applyPreset(r, active)}
                          className={presetChip(active)}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <DateRangeFilter
                    key={`${draft.issuedFrom}-${draft.issuedTo}`}
                    onDateRangeChange={handleIssueChange}
                    initialRange={{
                      from: fromISODate(draft.issuedFrom),
                      to: fromISODate(draft.issuedTo),
                    }}
                  />
                </div>

                {/* Aging — colored toggle pills */}
                <div className="mt-5">
                  <div className="mb-2.5 flex items-center justify-between">
                    <h4 className={sectionLabel + ' mb-0'}>Aging</h4>
                    {draft.agingTiers.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((d) => ({ ...d, agingTiers: [] }))
                        }
                        className="text-xs font-medium text-text-sub-600 hover:text-text-strong-950"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {AGING_PILLS.map(({ tier, label, active, inactive }) => {
                      const isActive = draft.agingTiers.includes(tier);
                      return (
                        <button
                          key={tier}
                          type="button"
                          onClick={() => toggleTier(tier)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            isActive ? active : inactive
                          }`}
                        >
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${AGING_DOT[tier]}`}
                          />
                          {label}
                          {isActive && (
                            <i className="ri-check-line ml-0.5 text-xs" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-between border-t border-stroke-soft-200 px-5 py-4">
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm font-medium text-text-sub-600 hover:text-text-strong-950"
                >
                  Reset
                </button>
                <Button variant="primary" size="medium" onClick={apply}>
                  Apply filter
                </Button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default InvoiceFilter;
